<template>
  <div class="container">
    <div class="video-content">
      <div class="main-content">
        <el-tabs v-model="activeTab" class="video-tabs" v-if="videoCategories.length > 0">
          <el-tab-pane 
            v-for="(category, categoryIndex) in videoCategories" 
            :key="categoryIndex"
            :label="category.title" 
            :name="`category-${categoryIndex}`"
          >
            <el-scrollbar class="video-scrollbar" height="40rem">
              <div class="video-grid">
                <div
                  v-for="(item, index) in category.dataList"
                  :key="item.id || index"
                  class="item"
                  @click="handleVideoClick(item, index)"
                >
                  <div class="news-poster">
                    <div class="poster-cover">
                      <img 
                        :src="item.cover || getDefaultCover()" 
                        :alt="item.title" 
                        class="poster"
                        @error="handleImageError"
                      >
                    </div>
                  </div>
                </div>
                <div v-if="category.dataList.length === 0" class="empty-tip">
                  暂无视频
                </div>
              </div>
            </el-scrollbar>
          </el-tab-pane>
        </el-tabs>
        <div v-else class="loading-container">
          <div class="loading-text">加载中...</div>
        </div>

        <button class="btn-more" @click="goToCustomerServiceView">
          查看更多
        </button>
                </div>
              </div>
    
    <!-- 视频播放弹窗 -->
    <div v-if="videoDialogVisible" class="video-modal" @click.self="handleCloseDialog">
      <div class="video-modal-content">
        <button class="video-close-btn" @click="handleCloseDialog">
          <el-icon>
            <Close />
          </el-icon>
        </button>
        <div class="video-player-wrapper">
          <div v-if="currentVideo?.loading" class="video-loading">
            <div class="loading-text">加载中...</div>
          </div>
          <video
            v-else-if="currentVideo?.videoUrl"
            ref="videoElement"
            class="video-player"
            controls
            playsinline
            preload="auto"
            crossorigin="anonymous"
            @canplay="onVideoCanPlay"
            @loadedmetadata="onVideoLoaded"
          >
            您的浏览器不支持视频播放
          </video>
          <div v-else class="video-error">
            视频加载失败，请稍后重试
          </div>
        </div>
                    </div>
                  </div>
                </div>
    
    <!-- 右下角 GIF 图片 -->
    <div class="gif-container" @click="goToCustomerServiceView">
      <div class="gif-text">点我获取更多视频</div>
      <img 
        src="https://wiseagent.oss-cn-shanghai.aliyuncs.com/website/upload/image/%E9%A3%9E%E4%B9%A620260123-181833.gif" 
        alt="动画" 
        class="gif-image"
      />
    </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, nextTick} from 'vue'
import {Close} from '@element-plus/icons-vue'
import {useRouter} from 'vue-router'
import {ElMessage} from 'element-plus'
import apiClient from '@/components/plugins/axios'
import axios from "axios";
import * as dashjsModule from 'dashjs'

// 获取 dashjs，兼容不同的导出方式
const dashjs = dashjsModule.default || dashjsModule

const router = useRouter()

// 当前激活的 tab
const activeTab = ref('category-0')

// 视频弹窗相关
const videoDialogVisible = ref(false)
const currentVideo = ref(null)
const videoElement = ref(null)
const API_BASE_URL = process.env.VUE_APP_WEB_URL;
// 视频分类数据
const videoCategories = ref([])
const loading = ref(false)

// dash.js 相关
let player = null
const isInitializing = ref(false)
const retryCount = ref(0)
const maxRetries = 3

// 全局错误处理函数引用
let globalErrorHandler = null
let globalRejectionHandler = null

// 获取视频列表
const fetchVideos = async () => {
  loading.value = true
  try {
    const response = await axios.get(`${API_BASE_URL}/website/getVideos`);
    if (response.data.code === 200 && response.data.data) {
      videoCategories.value = response.data.data.map((category, index) => ({
        ...category,
        name: `category-${index}`
      }))
      // 设置默认激活的 tab
      if (videoCategories.value.length > 0) {
        activeTab.value = 'category-0'
      }
    } else {
      ElMessage.error(response.data.message || '获取视频列表失败')
    }
  } catch (error) {
    console.error('获取视频列表失败:', error)
    ElMessage.error('获取视频列表失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 获取视频播放信息
const fetchVideoPlayUrl = async (videoId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/website/videoFiles/${videoId}`)
    if (response.data.code === 200 && response.data.data && response.data.data.length > 0) {
      // 返回第一个视频文件信息（如果有多个质量，可以选择最高质量的）
      const videoFile = response.data.data[0]
      return {
        videoId: videoFile.videoId,
        quality: videoFile.quality,
        format: videoFile.format,
        masterUrl: videoFile.masterUrl,
        bandwidth: videoFile.bandwidth,
        codecs: videoFile.codecs,
        width: videoFile.width,
        height: videoFile.height,
        fileSize: videoFile.fileSize,
        segmentDuration: videoFile.segmentDuration
      }
    } else {
      ElMessage.error(response.data.message || '获取视频播放地址失败')
      return null
    }
  } catch (error) {
    console.error('获取视频播放地址失败:', error)
    ElMessage.error('获取视频播放地址失败，请稍后重试')
    return null
  }
}

// 处理视频点击事件
const handleVideoClick = async (item) => {
  if (!item.id) {
    ElMessage.warning('视频信息不完整')
    return
  }
  
  // 显示加载状态
  videoDialogVisible.value = true
  currentVideo.value = {
    ...item,
    videoUrl: null,
    loading: true
  }
  
  // 获取视频播放地址
  const playData = await fetchVideoPlayUrl(item.dashVideoId)
  if (playData && playData.masterUrl) {
    currentVideo.value = {
      ...item,
      videoUrl: playData.masterUrl,
      quality: playData.quality,
      format: playData.format,
      bandwidth: playData.bandwidth,
      codecs: playData.codecs,
      width: playData.width,
      height: playData.height,
      loading: false
    }
    // 等待 DOM 更新后初始化 dash.js
    await nextTick()
    initDashPlayer()
  } else {
    currentVideo.value.loading = false
    ElMessage.error('获取视频播放地址失败')
  }
}

// 获取默认封面图
const getDefaultCover = () => {
  // 返回一个默认的占位图
  return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjExMyIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjExMyIgZmlsbD0iI2U1ZTdlYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7lm77niYfliqDovb3lpLHotKU8L3RleHQ+PC9zdmc+'
}

// 处理图片加载错误
const handleImageError = (event) => {
  event.target.src = getDefaultCover()
}

// 初始化 dash.js 播放器
const initDashPlayer = () => {
  if (!videoElement.value || !currentVideo.value?.videoUrl) {
    return false
  }
  
  // 防止重复初始化
  if (isInitializing.value) {
    return false
  }
  
  isInitializing.value = true
  
  try {
    // 如果已有播放器实例，先销毁
    if (player) {
      try {
        player.destroy()
      } catch (e) {
        // 忽略销毁错误
      }
      player = null
    }
    
    // 检查 dashjs 是否可用
    if (!dashjs) {
      throw new Error('dashjs 未正确导入')
    }
    
    // 尝试不同的方式访问 MediaPlayer
    let MediaPlayer
    if (dashjs.MediaPlayer) {
      MediaPlayer = dashjs.MediaPlayer
    } else if (dashjs.default && dashjs.default.MediaPlayer) {
      MediaPlayer = dashjs.default.MediaPlayer
    } else {
      throw new Error('无法找到 dashjs.MediaPlayer，请检查 dashjs 库是否正确安装')
    }
    
    // 创建 dashjs 播放器实例
    const playerInstance = MediaPlayer().create()
    player = playerInstance
    
    // 配置播放器设置（dashjs 5.x 的配置方式可能不同，如果失败可忽略）
    try {
      player.getSettings()
    } catch (e) {
      // 配置失败不影响播放器使用
    }
    
    // 监听网络错误
    player.on('networkError', (data) => {
      ElMessage({
        message: `网络错误: ${data.error?.message || '无法加载视频片段，请检查网络连接或 CORS 配置'}`,
        type: 'error',
        duration: 5000
      })
    })
    
    // 监听错误事件
    player.on('error', (error) => {
      let errorMessage = '视频播放出错'
      
      if (error.error) {
        const mediaError = error.error
        
        if (mediaError.code === 4) {
          // MEDIA_ERR_SRC_NOT_SUPPORTED
          if (mediaError.message && mediaError.message.includes('audio object type')) {
            errorMessage = '音频编解码器不匹配：音频对象类型与 MIME 类型中指定的不匹配。这是后端编码问题，需要重新生成 DASH 文件，确保音频编码参数正确。'
          } else if (mediaError.message && mediaError.message.includes('codec')) {
            errorMessage = '编解码器不匹配：init.mp4 和 segment.m4s 的编解码器参数不一致。需要重新生成 DASH 文件，确保编解码器信息一致。'
          } else {
            errorMessage = '视频格式不支持或 CORS 跨域问题，请检查服务器 CORS 配置'
          }
        } else if (mediaError.code === 3) {
          errorMessage = '视频解码错误，可能是编解码器不匹配'
        } else {
          errorMessage = `视频播放错误 (错误代码: ${mediaError.code})`
        }
      } else if (error.type === 'networkError' || error.message?.includes('network') || error.message?.includes('CORS')) {
        errorMessage = '网络请求失败：可能是 CORS 跨域问题。请检查服务器是否正确配置了 CORS 头（Access-Control-Allow-Origin 等）'
      }
      
      ElMessage({
        message: errorMessage,
        type: 'error',
        duration: 8000
      })
    })
    
    // 监听 video 元素的错误事件，阻止错误冒泡
    if (videoElement.value) {
      const videoErrorHandler = (e) => {
        e.preventDefault()
        e.stopPropagation()
      }
      videoElement.value.addEventListener('error', videoErrorHandler, true)
    }
    
    // 初始化播放器并加载视频
    // 第三个参数 false 表示不自动播放（避免需要用户交互的问题）
    try {
      player.initialize(videoElement.value, currentVideo.value.videoUrl, false)
      isInitializing.value = false
      retryCount.value = 0
      return true
    } catch (initError) {
      isInitializing.value = false
      throw new Error(`播放器初始化失败: ${initError.message || '未知错误'}`)
    }
  } catch (error) {
    isInitializing.value = false
    const errorMsg = error.message || error.toString() || '未知错误'
    
    // 如果是可重试的错误，尝试重试
    if (retryCount.value < maxRetries && (
      errorMsg.includes('Script error') || 
      errorMsg.includes('network') ||
      errorMsg.includes('CORS')
    )) {
      retryCount.value++
      setTimeout(() => {
        ElMessage({
          message: `初始化失败，正在重试 (${retryCount.value}/${maxRetries})...`,
          type: 'warning',
          duration: 2000
        })
        initDashPlayer()
      }, 1000 * retryCount.value) // 递增延迟
      return false
    }
    
    ElMessage({
      message: `初始化播放器失败: ${errorMsg}`,
      type: 'error',
      duration: 5000
    })
    return false
  }
}

// 关闭弹窗时的处理
const handleCloseDialog = () => {
  videoDialogVisible.value = false
  // 停止视频播放并销毁播放器
  setTimeout(() => {
    if (player) {
      try {
        player.destroy()
      } catch (error) {
        // 忽略销毁时的错误
      }
      player = null
    }
    if (videoElement.value) {
      videoElement.value.pause()
      videoElement.value.currentTime = 0
    }
    // 清空当前视频
    currentVideo.value = null
    retryCount.value = 0
    isInitializing.value = false
  }, 100)
}

// 视频加载完成
const onVideoLoaded = () => {
  // 视频元素已通过 ref 绑定
}

// 视频可以播放时自动播放
const onVideoCanPlay = () => {
  if (videoElement.value) {
    videoElement.value.play().catch(err => {
      console.log('自动播放失败:', err)
      // 某些浏览器可能阻止自动播放，这是正常行为
    })
  }
}

// 跳转到查询页面
const goToCustomerServiceView = () => {
  router.push('/customerServiceView')
}

// 设置全局错误处理
const setupGlobalErrorHandlers = () => {
  // 全局错误处理函数
  globalErrorHandler = (event) => {
    const errorMessage = event.message || event.error?.message || ''
    const errorSource = event.filename || event.source || ''
    
    // 捕获 Script error 和跨域错误
    if (errorMessage.includes('Script error') || 
        errorMessage.includes('script error') ||
        errorMessage === '' ||
        (errorSource && !errorSource.includes(window.location.origin) && errorSource !== '')) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      return true
    }
    
    // 捕获 video 相关错误
    if (event.target && (event.target.tagName === 'VIDEO' || event.target.tagName === 'SOURCE')) {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      return true
    }
    
    return false
  }
  
  globalRejectionHandler = (event) => {
    const reason = event.reason?.message || event.reason?.toString() || ''
    if (reason.includes('Script error') || 
        reason.includes('script error') ||
        reason === '') {
      event.preventDefault()
      event.stopPropagation()
      return true
    }
    return false
  }
  
  // 注册全局错误监听
  window.addEventListener('error', globalErrorHandler, true)
  window.addEventListener('unhandledrejection', globalRejectionHandler)
}

// 组件挂载时获取视频列表
onMounted(() => {
  setupGlobalErrorHandlers()
  fetchVideos()
})

// 组件卸载前清理播放器
onBeforeUnmount(() => {
  // 移除全局错误监听
  if (globalErrorHandler) {
    window.removeEventListener('error', globalErrorHandler, true)
    globalErrorHandler = null
  }
  if (globalRejectionHandler) {
    window.removeEventListener('unhandledrejection', globalRejectionHandler)
    globalRejectionHandler = null
  }
  
  // 清理播放器
  if (player) {
    try {
      player.destroy()
    } catch (error) {
      // 忽略销毁时的错误
    }
    player = null
  }
})
</script>

<style scoped>
.container {
  display: flex;
  padding-top: 12rem;
  background: #f5f6fa;
  overflow: hidden;
}

.video-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.main-content {
  width: 70rem;
  margin: 0 auto;
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.gif-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1000;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.gif-container:hover {
  transform: scale(1.1);
}

.gif-text {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  text-align: center;
  white-space: nowrap;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 3s ease infinite, float 2s ease-in-out infinite;
  text-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

@keyframes gradientShift {
  0%, 100% {
    background-position: 0% center;
  }
  50% {
    background-position: 100% center;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.gif-image {
  width: 150px;
  height: auto;
  display: block;
}

.video-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.video-grid {
  display: flex;
  flex-wrap: wrap;
}

.item {
  width: 20rem;
  height: 11.25rem;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 2rem;
  margin-right: 2rem;
}

.news-poster {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.poster-cover {
  width: 100%;
  height: 100%;
  position: relative;
}

.poster {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.item:hover .poster {
  transform: scale(1.05);
}

.news-title {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  height: 3rem;
  z-index: 2;
  display: flex;
  justify-content: center;
  background: rgba(255, 255, 255, 0.3);
}

.cover {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.5) 100%);
  transition: opacity 0.3s;
  z-index: 1;
  pointer-events: none;
}

.text {
  position: relative;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}

.line-ico {
  width: 3px;
  height: 16px;
  background: #ff9800;
  flex-shrink: 0;
}

.btn-more {
  display: block;
  margin: 1rem auto 0;
  text-align: center;
  width: 120px;
  height: 40px;
  line-height: 40px;
  background: #ff9800;
  color: #fff;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  flex-shrink: 0;
}

.btn-more:hover {
  background: #ff8c00;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
}

.btn-more:active {
  transform: translateY(0);
}

/* 视频弹窗样式 */
.video-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

.video-modal-content {
  position: relative;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
}


.video-close-btn {
  position: absolute;
  top: -50px;
  right: 0;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s;
}

.video-close-btn:hover {
  background: #fff;
  transform: scale(1.1);
}

.video-close-btn .el-icon {
  font-size: 20px;
  color: #333;
}

.video-player-wrapper {
  width: 100%;
  max-width: 100%;
  max-height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  position: relative;
}

.video-player {
  width: 100%;
  height: auto;
  max-width: 100%;
  max-height: 80vh;
  outline: none;
  display: block;
  object-fit: contain;
}

.empty-tip {
  width: 100%;
  text-align: center;
  padding: 3rem;
  color: #999;
  font-size: 16px;
}

.loading-container {
  width: 100%;
  height: 40rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-error {
  width: 100%;
  padding: 3rem;
  text-align: center;
  color: #999;
  font-size: 16px;
}

.video-loading {
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}
</style>
